// File: frontend/src/pages/KnowledgeGraph/styles/nodeStyles.ts
import { ENTITY_TYPE_COLORS, ENTITY_TYPE_SHAPES } from '../types';

/**
 * Builds a Cytoscape stylesheet array that adapts to light/dark mode.
 *
 * Classes used by ConstellationGraph for interaction states:
 * - `show-label`  → node label is visible (smart labels: selection, neighbors, top-N, zoom)
 * - `faded`       → element is dimmed (neighborhood focus on hover/selection)
 * - `hidden-type` → node hidden via the interactive legend filter
 */
export function buildCytoscapeStyles(mode: 'light' | 'dark'): cytoscape.StylesheetStyle[] {
  const isDark = mode === 'dark';
  const textColor = isDark ? '#e6e8f0' : '#1a1a2e';
  const edgeColor = isDark ? '#6b7280' : '#9ca3af';
  const inferredColor = isDark ? '#4dd0e1' : '#0891b2';
  const selectedBorder = '#7b4cff';
  const bgColor = isDark ? '#1a1d2e' : '#f5f5f5';

  const nodeStyles: cytoscape.StylesheetStyle[] = Object.entries(ENTITY_TYPE_COLORS).map(
    ([type, color]) => ({
      selector: `node[type="${type}"]`,
      style: {
        'background-color': color,
        shape: (ENTITY_TYPE_SHAPES[type] || 'ellipse') as any,
      },
    })
  );

  return [
    // --- Base node style (labels hidden by default; `show-label` reveals them) ---
    {
      selector: 'node',
      style: {
        label: 'data(name)',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'font-size': '11px',
        color: textColor,
        'text-opacity': 0,
        'text-outline-width': 2,
        'text-outline-color': bgColor,
        'text-max-width': '110px',
        'text-wrap': 'ellipsis',
        width: 'mapData(report_count, 1, 72, 28, 60)',
        height: 'mapData(report_count, 1, 72, 28, 60)',
        'border-width': 2,
        'border-color': 'data(color)',
        'border-opacity': 0.6,
        'overlay-padding': '4px',
        'transition-property': 'border-width, border-color, width, height, opacity',
        'transition-duration': 200,
      } as any,
    },
    // --- Per-type node colors ---
    ...nodeStyles,
    // --- Identity / Indicator smaller ---
    {
      selector: 'node[type="Identity"], node[type="Indicator"]',
      style: {
        width: 20,
        height: 20,
        'font-size': '9px',
      } as any,
    },
    // --- Observed edges ---
    {
      selector: 'edge[edge_kind="OBSERVED_REL"]',
      style: {
        'line-color': edgeColor,
        'target-arrow-color': edgeColor,
        'target-arrow-shape': 'triangle',
        'curve-style': 'bezier',
        width: 'mapData(mention_count, 1, 10, 1, 4)',
        opacity: 0.7,
        'arrow-scale': 0.8,
      } as any,
    },
    // --- Inferred edges ---
    {
      selector: 'edge[edge_kind="INFERRED_REL"]',
      style: {
        'line-color': inferredColor,
        'target-arrow-color': inferredColor,
        'target-arrow-shape': 'triangle',
        'line-style': 'dashed',
        'curve-style': 'bezier',
        width: 2,
        opacity: 'mapData(confidence, 0, 1, 0.3, 0.9)',
        'arrow-scale': 0.8,
      } as any,
    },
    // --- Smart labels: shown for selection, neighbors, top nodes, or deep zoom ---
    {
      selector: 'node.show-label',
      style: {
        'text-opacity': 1,
      } as any,
    },
    // --- Neighborhood focus: everything outside the focused neighborhood dims ---
    {
      selector: 'node.faded',
      style: {
        opacity: 0.12,
        'text-opacity': 0,
      } as any,
    },
    {
      selector: 'edge.faded',
      style: {
        opacity: 0.05,
      } as any,
    },
    // --- Legend type filter ---
    {
      selector: 'node.hidden-type',
      style: {
        display: 'none',
      } as any,
    },
    // --- Selected node: purple glow ring, label always visible, never faded ---
    {
      selector: 'node:selected',
      style: {
        opacity: 1,
        'border-width': 4,
        'border-color': selectedBorder,
        'border-opacity': 1,
        'overlay-color': selectedBorder,
        'overlay-opacity': 0.18,
        'text-opacity': 1,
      } as any,
    },
    // --- Edge hover / selection ---
    {
      selector: 'edge:selected',
      style: {
        'line-color': selectedBorder,
        'target-arrow-color': selectedBorder,
        width: 3,
        opacity: 1,
      } as any,
    },
  ];
}
